import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template177: Transporte SF - bloques separados (intro, bart, muni, cableCar, clipper, apps)
const Template177 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const isSmallScreen = useWindowSize().width <= 600;

	const getImageSrc = (id) =>
		page.images?.find((img) => img.id === id)?.src ?? "";
	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const introText = page.texts?.[1]?.content ?? "";
	const headerText = page.texts?.[0]?.content ?? "";
	const bart = sections[0] ?? {};
	const muni = sections[1] ?? {};
	const cableCar = sections[2] ?? {};
	const clipper = sections[3] ?? {};
	const apps = sections[4] ?? {};
	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		marginBottom: "12px",
		marginTop: 0,
		color: "#ca9904",
	};
	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100vh",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: headerBackgroundColor || "#F2B705",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 600,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 99999,
				}}
			>
				{headerText}
			</Box>

			<Box data-block-id="text-1" style={{ marginBottom: 10 }}>
				{/* ——— Intro ——— */}
				{introText && (
					<Box sx={{ padding: "12px 16px", fontSize: 13, color: contentColor }}>
						<Box component="p" sx={p}>
							{introText}
						</Box>
					</Box>
				)}

				{/* ——— BART ——— */}
				{(bart.title || bart.items?.length) && (
					<Box
						data-block-id="text-2"
						sx={{
							backgroundColor: "#fbf0cf",
							border: "2px solid #F2B705",
							borderRadius: "16px",
							padding: "16px",
							margin: "0 16px 16px 16px",
							position: "relative",
						}}
					>
						{bart.title && (
							<Box component="h3" sx={h3}>
								{bart.title}
							</Box>
						)}
						{bart.items?.length > 0 && (
							<Box
								component="ul"
								sx={{
									paddingLeft: "18px",
									margin: 0,
									fontSize: 13,
									width: isSmallScreen ? "60%" : "100%",
								}}
							>
								{bart.items.map((item, i) => (
									<li
										key={i}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label ? `${item.label} ` : "") +
														(item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
						{getImageSrc(bart.imageId) && (
							<Box
								component="img"
								src={getImageSrc(bart.imageId)}
								alt=""
								sx={{
									width: 110,
									position: "absolute",
									bottom: isSmallScreen ? 0 : -47,
									right: isSmallScreen ? -12 : -12,
								}}
							/>
						)}
						{bart.tooltipText && (
							<Box
								sx={{
									position: "absolute",
									bottom: isSmallScreen ? 70 : 30,
									right: isSmallScreen ? -10 : 0,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 12px",
									fontSize: 11,
									color: contentColor,
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 112,
								}}
								dangerouslySetInnerHTML={{ __html: bart.tooltipText }}
							/>
						)}
					</Box>
				)}

				{/* ——— Muni ——— */}
				{(muni.title || muni.items?.length) && (
					<Box data-block-id="text-3" sx={{ padding: "0 16px", fontSize: 13, color: contentColor }}>
						{muni.title && (
							<Box component="h3" sx={h3}>
								{muni.title}
							</Box>
						)}
						{muni.items?.length > 0 && (
							<Box component="ul" sx={{ paddingLeft: "18px" }}>
								{muni.items.map((item, i) => (
									<li
										key={i}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label
															? `<strong>${item.label}</strong> `
															: "") + (item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}

				{/* ——— Cable Car ——— */}
				{cableCar.text && (
					<Box data-block-id="text-4" sx={{ padding: "0 16px" }}>
						<Box
							sx={{
								backgroundColor: "#fff8db",
								border: "2px solid #f5c400",
								borderRadius: "12px",
								padding: "10px 12px",
								marginTop: 1,
								fontSize: 12,
								color: contentColor,
							}}
							dangerouslySetInnerHTML={{ __html: cableCar.text }}
						/>
					</Box>
				)}

				{/* ——— Clipper Card ——— */}
				{(clipper.title || clipper.items?.length) && (
					<Box
						data-block-id="text-5"
						sx={{
							padding: "12px 16px",
							fontSize: 13,
							color: contentColor,
							marginTop: 0.25,
						}}
					>
						{clipper.title && (
							<Box component="h3" sx={h3}>
								{clipper.title}
							</Box>
						)}
						{clipper.items?.length > 0 && (
							<Box component="ul" sx={{ paddingLeft: "18px" }}>
								{clipper.items.map((item, i) => (
									<li
										key={i}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label ? `${item.label} ` : "") +
														(item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}

				{/* ——— Apps ——— */}
				{(apps.title || apps.items?.length) && (
					<Box
						data-block-id="text-6"
						sx={{
							backgroundColor: "#fbf0cf",
							border: "2px solid #F2B705",
							borderRadius: "16px",
							padding: "16px",
							margin: "0 16px 16px 16px",
						}}
					>
						{apps.title && (
							<Box component="h3" sx={{ ...h3, marginBottom: 1 }}>
								{apps.title}
							</Box>
						)}
						{apps.items?.length > 0 && (
							<Box
								component="ul"
								sx={{ paddingLeft: "18px", margin: 0, fontSize: 13 }}
							>
								{apps.items.map((item, i) => (
									<li
										key={i}
										dangerouslySetInnerHTML={{
											__html:
												typeof item === "string"
													? item
													: (item.label ? `${item.label} ` : "") +
														(item.text ?? ""),
										}}
									/>
								))}
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template177.thumbnail = "/images/template-1.png";
export default Template177;
